import { router, usePage } from "@inertiajs/react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import { TourSpotlight } from "@/components/tours/tour-spotlight"
import { TOURS, tourByKey, tourFor, type Tour } from "@/lib/tours"
import { tour_completions_path } from "@/routes"
import type { SharedData } from "@/types"

interface TourContextValue {
  /** Start a tour by key. Used by the help centre and the checklist. */
  start: (key: string) => void
  running: Tour | null
  /** Tours this person has finished on this desk. */
  completed: string[]
  available: Tour[]
}

const TourContext = createContext<TourContextValue | null>(null)

export function useTour() {
  const context = useContext(TourContext)
  if (!context) throw new Error("useTour must be used inside TourProvider")

  return context
}

export function useTourOptional() {
  return useContext(TourContext)
}

/**
 * Guided tours (INCO-387).
 *
 * Mounted once in `PersistentLayout`, beside the assistant panel and the help
 * sheet, so a tour survives Inertia navigation between steps rather than being
 * torn down by the first link it asks you to follow.
 *
 * **It only interrupts an empty screen.** Auto-start fires once, on first visit
 * to a surface, and only when the server says that surface has nothing on it —
 * the same condition that renders the empty state. Nobody working a full
 * pipeline gets a spotlight thrown over it.
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const page = usePage<SharedData>().props
  const account = page.current_account
  const completedFromServer = useMemo(
    () => (page.completed_tours as string[] | undefined) ?? [],
    [page.completed_tours],
  )
  const surface = page.help_surface as string | undefined
  const surfaceIsEmpty = Boolean(page.surface_is_empty)

  const [running, setRunning] = useState<Tour | null>(null)
  // Mirrors the server so a tour finished this session is not offered again
  // before the next full page load.
  const [completed, setCompleted] = useState<string[]>(completedFromServer)
  // Auto-start is once per session per tour as well as once per person: a
  // partial reload must not restart a tour somebody just pressed ESC on.
  const [offered, setOffered] = useState<string[]>([])

  useEffect(() => setCompleted(completedFromServer), [completedFromServer])

  const start = useCallback((key: string) => {
    const tour = tourByKey(key)
    if (tour) setRunning(tour)
  }, [])

  const finish = useCallback(
    (state: "completed" | "skipped") => {
      const tour = running
      setRunning(null)
      if (!tour || !account) return

      if (state === "completed") setCompleted((keys) => [...new Set([...keys, tour.key])])

      // Recorded server-side, because brokers move between a laptop and a phone
      // through the day and localStorage would re-run a finished tour on the
      // second device. `preserveState` so finishing a tour does not throw away
      // whatever the page underneath was holding.
      router.post(
        tour_completions_path(account.uid),
        { tour_key: tour.key, state },
        { preserveState: true, preserveScroll: true, only: [] },
      )
    },
    [running, account],
  )

  // Auto-start: first visit to a surface that has nothing on it.
  useEffect(() => {
    if (running) return

    const tour = tourFor(surface)
    if (!tour) return
    if (!surfaceIsEmpty) return
    if (completed.includes(tour.key)) return
    if (offered.includes(tour.key)) return

    setOffered((keys) => [...keys, tour.key])
    setRunning(tour)
  }, [surface, surfaceIsEmpty, completed, offered, running])

  const value = useMemo(
    () => ({ start, running, completed, available: TOURS }),
    [start, running, completed],
  )

  return (
    <TourContext.Provider value={value}>
      {children}
      {running && <TourSpotlight tour={running} onFinish={finish} />}
    </TourContext.Provider>
  )
}
